'use client';

import { useTranslations } from 'next-intl';
import { Ellipsis, EyeOff, Flag } from 'lucide-react';
import { IconButton, Menu } from '@zillow/constellation-10';
import { LucideIcon } from '@/src/components/LucideIcon';
import { menuItemRow } from './listingActionMenu.styles';

type ListingActionMenuProps = {
  listingHidden: boolean;
  onHide: (e: React.MouseEvent) => void;
  onReport: (e: React.MouseEvent) => void;
  /** Extra handler on the trigger button — e.g. a card's `stopCardNavigation`. */
  onTriggerClick?: (e: React.MouseEvent) => void;
  tone?: 'brand' | 'critical' | 'neutral' | 'neutral-fixed';
  emphasis?: 'bare' | 'filled' | 'outlined';
  size?: 'lg' | 'md' | 'sm' | 'xs';
  iconSize?: 'lg' | 'md' | 'sm' | 'xl';
  className?: string;
  onImpact?: boolean;
  triggerId?: string;
  testIdSuffix?: string;
};

/**
 * Hide / Report overflow menu — an ellipsis trigger opening a dropdown with
 * Hide and Report items. Shared by `ListingCard` (SRP) and the HDP's
 * `ListingHeaderActions` header cluster, so both surfaces expose the same
 * affordance instead of the HDP burying these actions in a footer module.
 * Purely presentational: callers own the hide/report wiring (typically via
 * `useListingActions`/`useOptionalListingActionContext`) and pass in the
 * resulting handlers.
 */
export function ListingActionMenu({
  listingHidden,
  onHide,
  onReport,
  onTriggerClick,
  tone = 'neutral',
  emphasis = 'bare',
  size = 'sm',
  iconSize,
  className,
  onImpact,
  triggerId,
  testIdSuffix,
}: ListingActionMenuProps) {
  const t = useTranslations('hdp');
  const getTestId = (testId: string) => (testIdSuffix ? `${testId}-${testIdSuffix}` : testId);

  return (
    <Menu
      placement="bottom-end"
      trigger={
        <IconButton
          id={triggerId}
          type="button"
          shape="circle"
          tone={tone}
          emphasis={emphasis}
          size={size}
          onImpact={onImpact}
          className={className}
          title={t('listingOptions')}
          aria-label={t('listingOptions')}
          onClick={onTriggerClick}
          data-testid={getTestId('listing-action-menu')}
        >
          <LucideIcon icon={Ellipsis} size={iconSize} />
        </IconButton>
      }
      content={
        <Menu.Group>
          <Menu.Item onClick={onHide} data-testid={getTestId('button-hide')}>
            <span className={menuItemRow}>
              <LucideIcon icon={EyeOff} size="sm" />
              <Menu.ItemLabel>{listingHidden ? t('unhideListing') : t('hideListing')}</Menu.ItemLabel>
            </span>
          </Menu.Item>
          <Menu.Item onClick={onReport} data-testid={getTestId('button-report')}>
            <span className={menuItemRow}>
              <LucideIcon icon={Flag} size="sm" />
              <Menu.ItemLabel>{t('reportListing')}</Menu.ItemLabel>
            </span>
          </Menu.Item>
        </Menu.Group>
      }
    />
  );
}
